import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const EMPTY = { name: "", month: 1, day: "", birth_year: "", nicknames: "", user_id: "" };
export const birthdayLabel = (p) => (p.month ? `${MONTHS[p.month - 1]} ${p.day}${p.birth_year ? `, ${p.birth_year}` : ""}` : "not set");

const PersonForm = ({ initial, users, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    onSave({ name: f.name, month: Number(f.month), day: Number(f.day), birth_year: f.birth_year ? Number(f.birth_year) : null,
      nicknames: f.nicknames.split(",").map((s) => s.trim()).filter(Boolean), user_id: f.user_id || null });
  };
  return (
    <form className="people-form" onSubmit={submit} data-testid="person-form">
      <input className="login-input" placeholder="Name (e.g. Emma)" required value={f.name} onChange={set("name")} data-testid="person-name-input" />
      <div className="flex gap-2">
        <select className="login-input" value={f.month} onChange={set("month")} data-testid="person-month-select">
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <input className="login-input" type="number" min="1" max="31" placeholder="Day" required value={f.day} onChange={set("day")} data-testid="person-day-input" />
        <input className="login-input" type="number" min="1900" max="2100" placeholder="Birth year (optional)" value={f.birth_year} onChange={set("birth_year")} data-testid="person-year-input" />
      </div>
      <input className="login-input" placeholder="Nicknames, comma separated (optional)" value={f.nicknames} onChange={set("nicknames")} data-testid="person-nicknames-input" />
      <select className="login-input" value={f.user_id} onChange={set("user_id")} data-testid="person-user-select">
        <option value="">No linked account</option>
        {users.map((u) => <option key={u.user_id} value={u.user_id}>{`${u.name} · ${u.email}`}</option>)}
      </select>
      <div className="flex gap-2">
        <button className="btn-primary !py-1.5 !px-4 text-sm" data-testid="person-save-button">Save</button>
        {onCancel && <button type="button" className="btn-outline !py-1.5 !px-4 text-sm" onClick={onCancel} data-testid="person-cancel-button">Cancel</button>}
      </div>
    </form>
  );
};

export const PeopleList = ({ people, actions }) => (
  <div className="admin-table" data-testid="people-list">
    {!people.length && <p className="text-sm text-neutral-500" data-testid="people-empty">No people yet.</p>}
    {people.map((p) => (
      <div className="admin-row" key={p.id} data-testid={`person-row-${p.id}`}>
        <div className="min-w-0">
          <div className="text-sm text-neutral-900">{p.name}{p.nicknames?.length > 0 && <span className="text-neutral-500"> · {p.nicknames.join(", ")}</span>}</div>
          <div className="text-xs text-neutral-500 mt-0.5">Birthday {birthdayLabel(p)}{p.manual_count ? ` · ${p.manual_count} added by hand` : ""}</div>
        </div>
        {actions?.(p)}
      </div>
    ))}
  </div>
);

export const PeopleTab = () => {
  const [people, setPeople] = useState(null);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/people").then((r) => setPeople(r.data.people)), []);
  useEffect(() => { load(); api.get("/admin/users").then((r) => setUsers(r.data.users)); }, [load]);
  const save = async (body) => {
    try {
      if (editing === "new") await api.post("/people", body);
      else await api.patch(`/people/${editing.id}`, body);
      toast.success("Saved. Birthday Books update in about a minute.");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name}? Their Birthday Books will be removed too (photos stay).`)) return;
    await api.delete(`/people/${p.id}`);
    load();
  };
  if (!people) return null;
  return (
    <div data-testid="admin-people-tab">
      <p className="text-sm text-neutral-600 mb-4">Add family members (with or without an account) so their birthday photos get their own Birthday Book.</p>
      {editing ? (
        <PersonForm users={users} onSave={save} onCancel={() => setEditing(null)}
          initial={editing === "new" ? EMPTY : { ...editing, birth_year: editing.birth_year || "", nicknames: editing.nicknames.join(", "), user_id: editing.user_id || "" }} />
      ) : (
        <button className="btn-primary !py-1.5 !px-4 text-sm mb-4" onClick={() => setEditing("new")} data-testid="person-add-button">Add person</button>
      )}
      <PeopleList people={people} actions={(p) => (
        <div className="flex gap-2">
          <button className="btn-outline !py-1 !px-3 text-xs" onClick={() => setEditing(p)} data-testid={`person-edit-${p.id}`}>Edit</button>
          <button className="btn-outline !py-1 !px-3 text-xs" onClick={() => remove(p)} data-testid={`person-delete-${p.id}`}>Remove</button>
        </div>
      )} />
    </div>
  );
};
